import React, { useEffect, useMemo, useRef, useState } from "react";
import { toUrl } from "../../lib/config";
import "../../assets/styles/partners-filter.scss";

export interface Category {
  id: string;
  label: string;
}

export interface PartnerItem {
  id: string | number;
  name: string;
  logo: string;          // büyük görsel yolu
  logoSrcSet?: string;   // küçük/orta/büyük varyantlarınız varsa ekleyin
  logoSizes?: string;    // örn: "(max-width: 520px) 100vw, 33vw"
  category: string;
  href?: string;
  description?: string;
}

export interface PartnersFilterGalleryProps {
  categories: Category[];
  items: PartnerItem[];
  initialCategory?: string;
  showSearch?: boolean;
  accent?: string;
  green?: string;
  onCategoryChange?: (categoryId: string) => void;
}

const PartnersFilterGallery: React.FC<PartnersFilterGalleryProps> = ({
  categories,
  items,
  initialCategory,
  showSearch = true,
  accent = "#e30613",
  green = "#2ba87a",
  onCategoryChange,
}) => {
  const defaultCategory = initialCategory ?? categories?.[0]?.id ?? "";
  const [active, setActive] = useState<string>(defaultCategory);
  const [q, setQ] = useState<string>("");

  const [selected, setSelected] = useState<PartnerItem | null>(null);
  const lastFocusRef = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    let data = items;
    if (active) data = data.filter((it) => it.category === active);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      data = data.filter((it) => it.name.toLowerCase().includes(needle));
    }
    return data;
  }, [items, active, q]);

  const styleVars = {
    ["--accent" as any]: accent,
    ["--green" as any]: green,
  } as React.CSSProperties;

  const handleCategory = (id: string) => {
    setActive(id);
    onCategoryChange?.(id);
  };

  const openModal = (item: PartnerItem, ev?: React.MouseEvent | React.KeyboardEvent) => {
    if (ev?.currentTarget instanceof HTMLElement) {
      lastFocusRef.current = ev.currentTarget;
    } else {
      lastFocusRef.current = document.activeElement as HTMLElement | null;
    }
    setSelected(item);
  };

  const closeModal = () => {
    setSelected(null);
    setTimeout(() => lastFocusRef.current?.focus?.(), 0);
  };

  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeModal();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected]);

  useEffect(() => {
    if (selected && dialogRef.current) dialogRef.current.focus();
  }, [selected]);

const ImgWithFallback: React.FC<{ src?: string | null; alt: string; height?: number }> = ({ src, alt, height = 300 }) => {
    const baseIn = (src || '').trim();
    let finalSrc = '';
    if (!baseIn) {
      finalSrc = '';
    } else if (/^https?:\/\//i.test(baseIn)) {
      // absolute URL from backend
      finalSrc = baseIn;
    } else if (baseIn.startsWith('/api/')) {
      // backend-relative path -> absolute
      finalSrc = toUrl(baseIn);
    } else {
      // honor path as-is (no UUID rewriting)
      finalSrc = baseIn;
    }
    return (
      <img
        src={finalSrc}
        alt={alt}
        style={{ height, objectFit: 'contain', display: 'block' }}
        crossOrigin="anonymous"
      />
    );
  };


  return (
    <section className="partners-filter" style={styleVars}>
      <div className="pf-container">
        <aside className="pf-sidebar">
          <div className="pf-chips" role="tablist" aria-label="Kategoriler">
            {categories.map((c) => {
              const isActive = c.id === active;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`pf-chip ${isActive ? "active" : ""}`}
                  onClick={() => handleCategory(c.id)}
                  aria-pressed={isActive}
                  role="tab"
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </aside>

        <main className="pf-main">
          {showSearch && (
            <div className="pf-search" role="search">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ara (ör. 'AXA', 'Allianz'...)"
                aria-label="Kuruluş ara"
              />
              {q && (
                <button
                  className="pf-clear"
                  type="button"
                  onClick={() => setQ("")}
                  title="Temizle"
                  aria-label="Aramayı temizle"
                >
                  ×
                </button>
              )}
            </div>
          )}

          <div className="pf-grid">
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                className="pf-cardBtn"
                onClick={(ev) => openModal(p, ev)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    openModal(p, ev);
                  }
                }}
                aria-label={`${p.name} detayını aç`}
              >
                <div className="pf-card">
                  <div className="pf-logoWrap">
                    <ImgWithFallback src={p.logo} alt={p.name} height={140} />
                  </div>
                </div>
              </button>
            ))}
          </div>

          {!filtered.length && (
            <p className="pf-empty">
              Sonuç bulunamadı. Filtreleri değiştirin veya arama terimini temizleyin.
            </p>
          )}
        </main>
      </div>

      {selected && (
        <div className="pf-modal" role="dialog" aria-modal="true" aria-labelledby="pf-modal-title">
          <div className="pf-backdrop" onClick={closeModal} />
          <div className="pf-dialog" ref={dialogRef} tabIndex={-1}>
            <button className="pf-close" onClick={closeModal} aria-label="Kapat">×</button>
            <div className="pf-modal-body">
              <div className="pf-modal-logo">
                {/* <ImgWithFallback
                  src={selected.logo}
                  alt={`${selected.name} logosu`}
                  srcSet={selected.logoSrcSet}
                  sizes={selected.logoSizes}
                /> */}
                <ImgWithFallback src={selected.logo} alt="logo" height={150} />
              </div>
              <h3 id="pf-modal-title" className="pf-modal-title">{selected.name}</h3>
              <div className="pf-modal-text">
                {selected.description ? (
                  <p>{selected.description}</p>
                ) : (
                  <p>Bu kuruluşa ait ayrıntılı bilgi yakında eklenecektir.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PartnersFilterGallery;
